import { Response } from 'node-fetch';
import uri from 'urijs';
import url from 'url';

import { ObjectType } from '@vromo/models/helpers/models';
import { httpProtocol } from './constants';
import fetch from './fetch';
import { User } from '@vromo/models';
import { JobHistoryResponse } from '@vromo/dashboard/src/report/JobHistory/helpers/JobHistoryHelper';

export const devReportsURL = 'dev-reports-api.vromo.io';
export const prodReportsURL = 'reports-api.vromo.io';
export const devReportsURL_EU = 'test-reports-api.vromo.io';
export const prodReportsURL_EU = 'eu-1-reports-api.vromo.io';
export const prodReportsURL_IB = 'ib-us-1-reports-api.vromo.io';
export const loadTestReportsURL = 'load-test-1-reports-api.vromo.io';

export interface ReportsRequestParams {
    ['group-id']: number;
    timeFrom: number;
    drivers?: number[];
    groups?: number[];
    timeTo: number;
    fixed?: number;
    perCompleteJob?: number;
    perCancelled?: number;
    perKmOnJob?: number;
    perKmOffJob?: number;
    page?: number;
    size?: number;
    isImperial?: boolean;
}

export interface VehicleReportItem {
    id: number;
    driver: User;
    externalId: string;
    totalAvailabilityHours: number;
    shiftCount: number;
    jobsCompleted: number;
    jobsCancelled: number;
    jobsReOffered: number;
    totalTravel: number;
    offJobTravel: number;
    onJobTravel: number;
    avgRating: number;
    payEstimate: number;
    costPerJob: number;
    timeOnJob: number;
}

export interface DriverReportsResponse {
    _embedded: {
        vehicleReports: VehicleReportItem[];
    };
    page: {
        totalPages: number;
        totalElements: number;
        number: number;
        size: number;
    };
}

export interface JobHistoryReportsRequestParams {
    ['group-id']: number;
    timeFrom: number;
    vehicles?: number[];
    groups?: number[];
    timeTo: number;
    page?: number;
    size?: number;
    name?: string;
    sites?: number[];
    status?: string;
    policies?: number[];
    campaigns?: number[];
    isImperial?: boolean;
}

export interface JobReportsResponse {
    _embedded: {
        jobReports: JobHistoryResponse[];
    };
    page: {
        totalPages: number;
        totalElements: number;
        number: number;
        size: number;
    };
}

interface ApiEndpoint<R = any, B = ReportsRequestParams> {
    get: (queryParams?: ReportsRequestParams, token?: string) => Promise<R>;
}

const getReportsURLFromHost = (host: string) => {
    if (host.includes('test-k8s-api')) return devReportsURL_EU;
    if (host.includes('dev-api')) return devReportsURL;
    if (host.includes('eu-1-api')) return prodReportsURL_EU;
    if (host.includes('ib-us-1-api')) return prodReportsURL_IB;
    if (host.includes('load-test-1-api')) return loadTestReportsURL;
    return prodReportsURL;
};

/** vromo Auth api methods */
export class ReportsAPI {
    private _host: string;

    constructor(host = prodReportsURL) {
        this._host = getReportsURLFromHost(host);
    }

    /** R= result type, B= post body type */
    private _apiEndpoint<R = any, B = ObjectType>(path: string): ApiEndpoint<R, B> {
        const endpointUrl = url.resolve(`${httpProtocol}://${this._host}/`, path);

        return {
            get: async (queryParams, token) => {
                const processedUrl = uri(endpointUrl)
                    .query({ ...queryParams })
                    .toString();
                const getQueryData: Response = await fetch(processedUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: '*/*',
                        Authorization: token
                    }
                });
                return (await getQueryData.json()) as R;
            }
        };
    }

    get getDriverReports(): ApiEndpoint<null, DriverReportsResponse> {
        return this._apiEndpoint(`/reports/vehicles`);
    }

    get getDriverReportsCSV(): ApiEndpoint<{ reportDownloadLink: string }, any> {
        return this._apiEndpoint(`/reports/vehicles/downloads`);
    }

    get getJobHistory(): ApiEndpoint<null, any> {
        return this._apiEndpoint(`/reports/jobs/history`);
    }

    get getJobHistoryReportsCSV(): ApiEndpoint<{ reportDownloadLink: string }, any> {
        return this._apiEndpoint(`/reports/jobs/history/downloads`);
    }
}

/** rest api Error ( includes error body object response details ) */
export class ApiError<B> extends Error {
    protected _response: B;

    constructor(response: B, payload: Error | string) {
        super((payload as Error).message || (payload || 'undefined err').toString());
        this._response = response;
    }

    get response(): B {
        return this._response;
    }
}

declare global {
    interface Window {
        ReportsAPI: ReportsAPI;
    }
}
